import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", role: "student", student_email: "",
  });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register({
        ...form,
        email: form.email.trim().toLowerCase(),
        student_email: form.student_email.trim().toLowerCase() || null,
      });
      toast.success("Kayıt tamam!");
      nav(u.role === "parent" ? "/veli" : "/panel", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md ela-fade-in">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-[#FF6B6B] flex items-center justify-center text-white">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading font-bold text-xl text-slate-900 leading-none">Ela AI Akademi</div>
            <div className="text-xs text-slate-500 mt-1">Yeni hesap</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Aramıza katıl</h1>
          <p className="text-slate-500 text-sm mt-1">Birkaç bilgi yeterli.</p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div className="space-y-1.5">
              <Label className="text-slate-700">Ad Soyad</Label>
              <Input required value={form.full_name} onChange={onChange("full_name")}
                data-testid="register-fullname-input" className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700">Email</Label>
              <Input type="email" required value={form.email} onChange={onChange("email")}
                data-testid="register-email-input" className="rounded-xl h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700">Şifre</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={onChange("password")}
                data-testid="register-password-input" className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Rol</Label>
              <RadioGroup
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
                className="grid grid-cols-2 gap-2"
              >
                <label className={`cursor-pointer rounded-xl border p-3 text-sm flex items-center gap-2 ${form.role === "student" ? "border-[#FF6B6B] bg-rose-50" : "border-slate-200"}`}>
                  <RadioGroupItem value="student" data-testid="register-role-student" />
                  <span>🎓 Öğrenci</span>
                </label>
                <label className={`cursor-pointer rounded-xl border p-3 text-sm flex items-center gap-2 ${form.role === "parent" ? "border-[#FF6B6B] bg-rose-50" : "border-slate-200"}`}>
                  <RadioGroupItem value="parent" data-testid="register-role-parent" />
                  <span>👨‍👩‍👧 Veli</span>
                </label>
              </RadioGroup>
            </div>
            {form.role === "parent" && (
              <div className="space-y-1.5">
                <Label className="text-slate-700">Öğrencinin Email'i (opsiyonel)</Label>
                <Input value={form.student_email} onChange={onChange("student_email")}
                  placeholder="ela@akademi.com"
                  data-testid="register-student-email-input" className="rounded-xl h-11" />
              </div>
            )}

            <Button type="submit" disabled={busy}
              data-testid="register-submit-btn"
              className="w-full h-11 rounded-xl bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-medium">
              {busy ? "Kaydediliyor…" : "Hesap Oluştur"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Zaten hesabın var mı?{" "}
            <Link to="/giris" className="text-[#FF6B6B] font-medium hover:underline">Giriş yap</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
